import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tareasBaseService } from '../services/tramiteTareasService';
import { aplicarOrden } from '@/presupuestos/utils/orden';
import type { TareaBase, TareaBaseFormData } from '../types';

export const TAREAS_BASE_QUERY_KEY = ['tramite-tareas-base'] as const;

/** Autogestión de la base genérica de tareas. */
export const useTareasBase = () => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: TAREAS_BASE_QUERY_KEY,
    queryFn: () => tareasBaseService.listar(),
  });

  const invalidar = () => queryClient.invalidateQueries({ queryKey: TAREAS_BASE_QUERY_KEY });

  const crearMutation = useMutation({
    mutationFn: (payload: TareaBaseFormData) => tareasBaseService.crear(payload),
    onSuccess: invalidar,
  });

  const actualizarMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<TareaBaseFormData> }) =>
      tareasBaseService.actualizar(id, data),
    onSuccess: invalidar,
  });

  const eliminarMutation = useMutation({
    mutationFn: (id: string) => tareasBaseService.eliminar(id),
    onSuccess: invalidar,
  });

  const reordenarMutation = useMutation({
    mutationFn: (items: string[]) => tareasBaseService.reordenar(items),
    // Si falló, también se recarga: vuelve el orden guardado.
    onSettled: invalidar,
  });

  /**
   * Guarda el orden arrastrado. Se aplica en la caché antes de la request para
   * que la lista no vuelva un instante al orden anterior.
   */
  const reordenarTareasBase = (ids: string[]) => {
    queryClient.setQueryData<TareaBase[] | undefined>(TAREAS_BASE_QUERY_KEY, (actual) =>
      actual ? aplicarOrden(actual, ids) : actual
    );

    return reordenarMutation.mutateAsync(ids);
  };

  return {
    tareasBase: data ?? [],
    isLoading,
    error,
    crearTareaBase: crearMutation.mutateAsync,
    actualizarTareaBase: actualizarMutation.mutateAsync,
    eliminarTareaBase: eliminarMutation.mutateAsync,
    reordenarTareasBase,
    isGuardando:
      crearMutation.isPending ||
      actualizarMutation.isPending ||
      eliminarMutation.isPending ||
      reordenarMutation.isPending,
  };
};

export default useTareasBase;
